import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useAnalytics } from '../hooks/useAnalytics';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_no-sub-pdf/artifacts/8avttwow_logohexoworkssanstitre.png";

export default function RegisterPage() {
  const { t, lang } = useI18n();
  const { register } = useAuth();
  const { trackSignup } = useAnalytics();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(email, password, name);
      trackSignup();
      toast.success('Account created');
      navigate('/account');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="register-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-500/5 via-transparent to-transparent" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-sm">
        <Link to="/" className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground text-sm mb-8 transition-colors" data-testid="register-back-link">
          <ArrowLeft className="w-4 h-4" />Back
        </Link>
        <div className="flex items-center gap-2.5 mb-8">
          <img src={LOGO_ICON} alt="HexoPDF" className="w-8 h-8" />
          <span className="font-heading font-bold text-xl text-foreground">Hexo<span className="text-blue-500">PDF</span></span>
        </div>
        <h1 className="font-heading text-2xl font-semibold text-foreground mb-1" data-testid="register-title">{t('auth.register_title')}</h1>
        <p className="text-muted-foreground text-sm mb-8">{t('auth.register_subtitle')}</p>
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
          <div>
            <Label className="text-muted-foreground text-sm mb-1.5 block">{t('auth.name')}</Label>
            <Input type="text" value={name} onChange={(e) => setName(e.target.value)} className="bg-card border-border focus:border-blue-500 text-foreground h-11 rounded-lg" data-testid="register-name-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-sm mb-1.5 block">{t('auth.email')}</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-card border-border focus:border-blue-500 text-foreground h-11 rounded-lg" data-testid="register-email-input" />
          </div>
          <div>
            <Label className="text-muted-foreground text-sm mb-1.5 block">{t('auth.password')}</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="bg-card border-border focus:border-blue-500 text-foreground h-11 rounded-lg" data-testid="register-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 text-white rounded-full h-11 font-medium shadow-[0_0_15px_rgba(59,130,246,0.4)] hover:shadow-[0_0_25px_rgba(59,130,246,0.6)] transition-all" data-testid="register-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('auth.register_btn')}
          </Button>
        </form>
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground/60 uppercase">{lang === 'fr' ? 'ou' : 'or'}</span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <a href={`https://demobackend.emergentagent.com/auth/v1/env/oauth/google?redirect_url=${encodeURIComponent(window.location.origin + '/auth/callback')}`}
          className="flex items-center justify-center gap-2.5 w-full h-11 rounded-full border border-border bg-card hover:bg-muted text-foreground/80 hover:text-foreground text-sm font-medium transition-all" data-testid="register-google-btn">
          <svg viewBox="0 0 24 24" className="w-5 h-5"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          {lang === 'fr' ? "S'inscrire avec Google" : 'Sign up with Google'}
        </a>
        <p className="text-sm text-muted-foreground mt-6 text-center">
          {t('auth.has_account')}{' '}
          <Link to="/login" className="text-blue-500 hover:text-blue-400 transition-colors" data-testid="register-login-link">{t('auth.login_link')}</Link>
        </p>
      </motion.div>
    </div>
  );
}
